import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { NavTopBar, Table } from "@organisms"
import TableViewPhase from "@organisms/Table/TableViewControls/TableViewPhase/TableViewPhase"
import TableViewPhases from "@organisms/Table/TableViewControls/TableViewPhases/TableViewPhases"

import { CREDIT_NOTES_ROUTES } from "@routes/creditNotes"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    CREDIT_NOTES_LIST_CONTROLS_ACTIVE_TAB,
    CREDIT_NOTES_LIST_CONTROLS_KEYWORD_SEARCH,
    CREDIT_NOTES_LIST_CONTROLS_ORDERING,
    CREDIT_NOTES_LIST_CONTROLS_PHASE,
} from "@constants/storage"

import { CreditNoteTableViewTabs } from "./CreditNoteTableViewTabs"

export default function CreditNoteTableView() {
    const [createRoute] = useSlugExtractor([CREDIT_NOTES_ROUTES.CREATE])

    const { hasPermission } = useHasAccess()

    return (
        <Table.Provider
            activePhaseStorageKey={CREDIT_NOTES_LIST_CONTROLS_PHASE}
            activeTabStorageKey={CREDIT_NOTES_LIST_CONTROLS_ACTIVE_TAB}
            searchKeywordsStorageKey={CREDIT_NOTES_LIST_CONTROLS_KEYWORD_SEARCH}
            orderingStorageKey={CREDIT_NOTES_LIST_CONTROLS_ORDERING}
            listTitle="Credit Notes"
            objectType="credit_note"
            objectIndexName="CreditNote"
            objectName="Credit Note"
            defaultActivePhase="open"
            objectCreateRoute={createRoute}
            isRowClickable={hasPermission("invoices_view_permission", PERMISSION_LEVEL.FULL)}
            isEmptyStateCreateButtonVisible={hasPermission("invoices_create_permission", PERMISSION_LEVEL.RESTRICTED)}
        >
            <NavTopBar.Portal>
                <NavTopBar.Body>
                    <NavTopBar.BodyStart>
                        <TableViewPhases>
                            <TableViewPhase phase="open" title="Open" />
                            <TableViewPhase phase="closed" title="Closed" />
                        </TableViewPhases>
                        <NavTopBar.VerticalDivider />
                        <CreditNoteTableViewTabs />
                    </NavTopBar.BodyStart>
                    <NavTopBar.BodyEnd>
                        <Table.SearchInput />
                        <Table.ViewMenu />
                    </NavTopBar.BodyEnd>
                </NavTopBar.Body>
            </NavTopBar.Portal>
            <Table.Table>
                <Table.Column<CreditNote>
                    accessorKey="id"
                    header="ID"
                    size={144}
                    cell={({ row }) => <Table.Cells.IDCell id={row.original.custom_id || row.original.id} />}
                    enableSorting={true}
                    enablePinning={true}
                    dataType="alphabetical"
                />
                <Table.Column<CreditNote>
                    accessorKey="invoice_id"
                    header="Invoice ID"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.IDCell id={getValue() || "—"} />}
                    enableSorting={true}
                    enablePinning={true}
                    dataType="alphabetical"
                />
                <Table.Column<CreditNote>
                    accessorKey="client_name"
                    header="Client Name"
                    size={288}
                    cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                    enableSorting={true}
                    enablePinning={true}
                    dataType="alphabetical"
                />
                <Table.Column<CreditNote>
                    accessorKey="status_label"
                    header="Status"
                    size={176}
                    cell={({ getValue }) => <Table.Cells.StatusCell objectType="credit_note" status={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="other"
                    loadingType="badge"
                />
                <Table.Column<CreditNote>
                    accessorKey="credit_note_type_label"
                    header="Type"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.TextCell content={getValue() || "—"} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="alphabetical"
                />
                <Table.Column<CreditNote>
                    accessorKey="labels"
                    header="Labels"
                    size={240}
                    cell={({ getValue }) => (
                        <Table.Cells.BadgesCell
                            badges={(getValue() as CreditNoteLabel[])?.map((label: CreditNoteLabel) => label.name)}
                        />
                    )}
                    enableSorting={false}
                    enablePinning={false}
                    dataType="other"
                    loadingType="badge"
                />
                <Table.Column<CreditNote>
                    accessorKey="total"
                    header="Total"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.NumericalDataCell value={getValue()} type="currency" />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
                <Table.Column<CreditNote>
                    accessorKey="date_issued"
                    header="Date Issued"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="timing"
                />
                <Table.Column<CreditNote>
                    accessorKey="last_modified_at"
                    header="Last Modified"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="timing"
                />
            </Table.Table>
        </Table.Provider>
    )
}
